import { useEffect, useRef, useContext, createContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import * as subscriptions from "graphql/subscriptions";
import { getLogger } from "utils";

const logger = getLogger(true, "useOrgSubscription()");

export const AmplifyPubSubContext = createContext(null);

/**
 * Hook for subscribing to `orgId` updates through aws-amplify.
 * Internally processes update onto the store and unsubscribes on component unmount.
 * This should only be called once at a top level component.
 *
 * @param {string} orgId - OrgId to subscribe to
 * @returns {AmplifyPubSubContext}
 */
export function useAmplifyOrgSubscriptionPublisher(orgId) {
  const subscribers = useRef(new Set());

  useEffect(() => {
    if (!orgId) {
      return;
    }
    const prefixedOrgId = "orgId:" + orgId;
    logger.info(
      "Initializing Amplify Event Provider for Org <<",
      prefixedOrgId,
      ">>"
    );
    const orgSubscription = API.graphql(
      graphqlOperation(subscriptions.subscribe, {
        name: prefixedOrgId,
      })
    ).subscribe({
      next: (data) => {
        if (subscriptionMessageIsValid(data)) {
          const parsedData = parseSubscriptionPayload(data);
          logger.info(
            "Relaying Update to subscribers. Count: ",
            subscribers.current,
            parsedData
          );
          subscribers.current.forEach((cb) => cb(parsedData));
        }
      },
      error: (error) => console.warn(error),
    });

    const currSubs = subscribers.current;

    // unsubcribe on unmount
    return () => {
      currSubs.clear();
      orgSubscription.unsubscribe();
    };
  }, [orgId]);

  const subscribe = (cb) => {
    subscribers.current.add(cb);
    logger.info(
      "Attaching Subscription. Current Count: ",
      subscribers.current.size
    );
    return () => {
      subscribers.current.delete(cb);
      logger.info(
        "Detaching Subscription. Current Count: ",
        subscribers.current.size
      );
    };
  };

  return { subscribe };
}

/**
 * For any pages or components that consume the `AmplifyPubSubContext` (all that are authed, see
 * `AuthedRouterPage.jsx`), a subscription can be added to respond to updates scoped by the current
 * organization.
 *
 * @param {(updatePayload: Object) => void} cb - The callback to run when an update from Amplify is received.
 */
export function useAmplifyOrgSubscription(cb) {
  const ctx = useContext(AmplifyPubSubContext);
  if (!ctx) {
    throw new Error("Could not find Amplify Subscription Context");
  }
  useEffect(() => ctx.subscribe(cb), [ctx, cb]);
}

function subscriptionMessageIsValid(payload = {}) {
  return !!payload?.value?.data?.subscribe?.data;
}

function parseSubscriptionPayload(payload = {}) {
  return JSON.parse(payload.value.data.subscribe.data, null, 2);
}
